/*
eWorldES Proprietary Software
Copyright (C) 2025 eWorldES. All rights reserved.

This software is proprietary to eWorldES and is protected by copyright laws and international treaties.
You may not use, copy, modify, distribute, or disclose this software without explicit written permission from eWorldES.
Any unauthorized use or reproduction of this software is strictly prohibited and may result in legal action.

License Agreement:
By using this software, you agree to be bound by the terms of the eWorldES Proprietary Software License Agreement.
For a copy of the agreement, visit https://eworldes.com/ or contact eWorldES directly for more information.

This software is provided "AS IS" without warranty of any kind, express or implied, including but not limited to the warranties of
merchantability, fitness for a particular purpose, or non-infringement. In no event shall eWorldES be liable for any damages
arising out of the use of this software, even if eWorldES has been advised of the possibility of such damages.

For inquiries or requests regarding this software, please contact eWorldES at:
Email: info@eworldes.com
Website: https://eworldes.com/
*/
import { useEffect } from "react";
import { useFormContext } from "react-hook-form";
import { InputFieldProps } from "../../types/InputProps";
import { Grid } from "../Grid";
import { renderInputField } from "./InputFieldMapper";
import { Box, Divider, useMediaQuery, useTheme } from "@mui/material";

type FormInputFieldsProps = {
  fields: InputFieldProps[];
  leftPanelCol?: number;
  rightPanelCol?: number;
  showDivider?: boolean;
  dividerColor?: string;
};

export type Dependency = {
  ref: string | string[];
  value: string | boolean | number | object | Function;
};

export const renderFields = (fields: InputFieldProps[]) => {
  return (
    <Grid container spacing={3}>
      {fields
        .filter((field) => field.show)
        .map((input, index) => (
          <Grid
            key={`${input.name || "field"}-${index}`}
            size={{
              md: input.fieldCol || 3,
              sm: input.fieldCol || 6,
              xs: 12,
            }}
          >
            {renderInputField(input)}
          </Grid>
        ))}
    </Grid>
  );
};

const FormInputFields = ({
  fields,
  leftPanelCol,
  rightPanelCol,
  showDivider,
  dividerColor,
}: FormInputFieldsProps) => {
  const { watch, setValue } = useFormContext();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const leftInputFields = fields
    .map((field) => {
      const isVisible =
        field.align === "left" && showDependentField(field, watch);
      return { ...field, show: isVisible };
    })
    .filter((field) => field.show);

  const rightInputFields = fields
    .map((field) => {
      const isVisible =
        field.align !== "left" && showDependentField(field, watch);
      return { ...field, show: isVisible };
    })
    .filter((field) => field.show);

  const isEmpty = (val: any) => {
    if (val === null || val === "") return true;
    if (typeof val === "object") {
      return Object.values(val).every((v) => v === null || v === "");
    }
    return false;
  };

  // Single useEffect to handle all field resets
  useEffect(() => {
    fields.forEach((field) => {
      const isVisible = showDependentField(field, watch);
      const value = watch(field.name);

      if (!isVisible && !isEmpty(value)) {
        const resetValue = Array.isArray(value) ? [] : "";
        setValue(field.name, resetValue, { shouldValidate: false });
      }
    });
  }, [fields, watch, setValue]); // Dependencies: fields, watch, setValue

  const layout = showDivider && !isMobile ? (
  <Box display="flex" flexDirection="row">
    <Box flex={leftPanelCol}>
      {leftInputFields.length > 0 && renderFields(leftInputFields)}
    </Box>
    <Divider
      orientation="vertical"
      flexItem
      sx={{
        borderLeft: "1px solid",
        borderColor: dividerColor || "transparent",
        mx: 2,
        mt:3,
      }}
    />
    <Box flex={rightPanelCol}>
      {rightInputFields.length > 0 && renderFields(rightInputFields)}
    </Box>
  </Box>
) : (
  <Grid container spacing={3}>
    {leftInputFields.length > 0 && (
      <Grid size={{ xs: 12, md: leftPanelCol }}>
        {renderFields(leftInputFields)}
      </Grid>
    )}
    {rightInputFields.length > 0 && (
      <Grid size={{ xs: 12, md: rightPanelCol }}>
        {renderFields(rightInputFields)}
      </Grid>
    )}
  </Grid>
);
  return <>{layout}</>;
};

const showDependentField = (field: InputFieldProps, watch: any): boolean => {
  if (!field.dependency || !field.show) {
    return field.show ?? true;
  }

  const { ref, value } = field.dependency;
  const refs = Array.isArray(ref) ? ref : [ref];
  const valuesToCheck = watch(refs);

  if (typeof value === "function") {
    return value(valuesToCheck);
  }

  if (Array.isArray(value)) {
    if (refs.length === 1) {
      // Single ref, multiple possible values
      return value.includes(valuesToCheck[0]);
    }

    // Multiple refs, match values at corresponding positions
    return refs.every((_, index) =>
      value[index] === undefined || value[index] === null
        ? true
        : valuesToCheck[index] == value[index],
    );
  }

  if (["string", "number", "boolean"].includes(typeof value)) {
    return valuesToCheck.some((v: any) => v == value);
  }

  if (typeof value === "object" && value !== null) {
    return refs.every((refKey: string, index: number) => {
      const expectedValue = (value as Record<string, any>)[refKey];
      return expectedValue === undefined || expectedValue === null
        ? true
        : valuesToCheck[index] == expectedValue;
    });
  }

  return true;
};

export default FormInputFields;